import { Injectable } from "@angular/core";
import { Long } from "shared/cubiq-model-multi-asset";
import { SystemMonitorEventImpl } from "shared/cubiq-model-multi-asset";
import { SystemMonitorLookupService } from "shared/cubiq-services/lookup/model/impl/SystemMonitorLookupService";
import { BFXNotificationSubType } from "shared/cubiq-services/model/core/notifications/BFXNotificationSubType";
import {
  CONNECTION_DOWN,
  CONNECTION_OUT_OF_SERVICE,
  CONNECTION_UP,
  SYSMON_EVENT_FQDN,
  SYSMON_EVENT_FQDN_HUB_PREFIX,
  SYSMON_EVENT_STATUS,
} from "../lookup/model/impl/SystemMonitorLookup.constants";

export interface HubConnectionStatusListener {
  getTgId(): Long;
  onHubStatusChange(tgId: Long, status: TGStatus);
}

export enum TGStatus {
  ONLINE = 0,
  TBD = 1,
  OFFLINE = 2,
}

@Injectable()
export class HubConnectionService {
  subscriptions: Map<string, Set<HubConnectionStatusListener>> = new Map<
    string,
    Set<HubConnectionStatusListener>
  >();

  public constructor(protected sysMonService: SystemMonitorLookupService) {
    this.sysMonService.getEventSubject().subscribe(this.onSystemMonitorUpdateEvent.bind(this));
  }

  protected onSystemMonitorUpdateEvent($event: SystemMonitorEventImpl) {
    if (this.isAHubEvent($event)) {
      let [tgId, tgStatus] = this.extractTGDataFrom($event);
      this.notify(tgId, tgStatus);
    }
  }

  isAHubEvent(event: SystemMonitorEventImpl): boolean {
    const fqdn: string = event.dataMap.get(SYSMON_EVENT_FQDN);
    return fqdn && fqdn.startsWith(SYSMON_EVENT_FQDN_HUB_PREFIX);
  }

  extractTGDataFrom($event: SystemMonitorEventImpl): [Long, TGStatus] {
    let fqdn: string = $event.dataMap.get(SYSMON_EVENT_FQDN);
    let tgIdStr: string = fqdn.split(".")[3]; // we want the last, 4th, bit
    let tgId: Long = new Long(Number.parseInt(tgIdStr));
    let status: TGStatus = this.getTGStatusFrom($event);
    return [tgId, status];
  }

  notify(tgId: Long, tgStatus: TGStatus) {
    let tgCallbacks: Set<HubConnectionStatusListener> = this.subscriptions.get(tgId.toString());

    if (null != tgCallbacks) {
      tgCallbacks.forEach((tgCallback) => {
        tgCallback.onHubStatusChange(tgId, tgStatus);
      });
    }
  }

  /**
   * Regiter the provided callback to notify connection changes to the supplied TG
   * Also prime the callback by loading the current status and calling the listener
   */
  subscribe(listener: HubConnectionStatusListener) {
    let interest = listener.getTgId().toString();

    let tgSubs: Set<HubConnectionStatusListener> = this.subscriptions.get(interest);

    if (null == tgSubs) {
      tgSubs = new Set<HubConnectionStatusListener>();
      this.subscriptions.set(interest, tgSubs);
    }

    tgSubs.add(listener);

    listener.onHubStatusChange(listener.getTgId(), this.getCurrentHubStatus(listener.getTgId()));
  }

  getCurrentHubStatus(tgId: Long): TGStatus {
    let fqdn: string = SYSMON_EVENT_FQDN_HUB_PREFIX + tgId;
    let curr: BFXNotificationSubType = this.sysMonService.getCurrentStatus(fqdn);
    switch (curr) {
      case BFXNotificationSubType.CONNECTION_MONITOR_CONNECTED:
        return TGStatus.ONLINE;
      case BFXNotificationSubType.CONNECTION_MONITOR_DISCONNECTED:
      case BFXNotificationSubType.CONNECTION_MONITOR_OUT_OF_SERVICE:
        return TGStatus.OFFLINE;
      default:
        return TGStatus.TBD;
    }
  }

  unsubscribe(listener: HubConnectionStatusListener) {
    let interest = listener.getTgId().toString();
    let tgSubs: Set<HubConnectionStatusListener> = this.subscriptions.get(interest);
    if (null != tgSubs) {
      tgSubs.delete(listener);
    }
  }

  getTGStatusFrom(event: SystemMonitorEventImpl): TGStatus {
    let ret = null;
    const status: string = event.dataMap.get(SYSMON_EVENT_STATUS);
    if (status === CONNECTION_UP) {
      ret = TGStatus.ONLINE;
    } else if (status === CONNECTION_DOWN || status === CONNECTION_OUT_OF_SERVICE) {
      ret = TGStatus.OFFLINE;
    } else {
      ret = TGStatus.TBD;
    }
    return ret;
  }
}
